import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { money } from "../lib/helpers";
import { Field, NativeSelect } from "./Common";
import { ProfileFields, PROFILE_BLANK } from "./ProfileFields";

function ClientPicker({ value, onChange }) {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState({ first_name: "", last_name: "", phone: "", email: "", ...PROFILE_BLANK });

  useEffect(() => {
    if (value || adding) return;
    const h = setTimeout(() => api.get("/clients", { params: { q } }).then((r) => setList(r.data.slice(0, 6))), 250);
    return () => clearTimeout(h);
  }, [q, value, adding]);

  const create = async () => {
    try {
      const { data } = await api.post("/clients", nc);
      onChange(data);
      setAdding(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border px-3 py-2" data-testid="selected-client">
        <span className="font-medium">{value.first_name} {value.last_name} <span className="text-muted-foreground text-xs">{value.client_code}</span></span>
        <button type="button" className="text-xs text-primary" data-testid="change-client-btn" onClick={() => onChange(null)}>{t("edit")}</button>
      </div>
    );
  }
  if (adding) {
    return (
      <div className="grid sm:grid-cols-2 gap-2 rounded-md border p-3" data-testid="quick-client-form">
        {["first_name", "last_name", "phone", "email"].map((k) => (
          <Input key={k} placeholder={t(k)} data-testid={`quick-client-${k}`} value={nc[k]} onChange={(e) => setNc({ ...nc, [k]: e.target.value })} />
        ))}
        <ProfileFields value={nc} onChange={setNc} t={t} testPrefix="quick-client" />
        <Button type="button" size="sm" data-testid="quick-client-save" onClick={create} disabled={!nc.first_name}>{t("create")}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)}>{t("cancel")}</Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input data-testid="client-search-input" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
        <Button type="button" variant="outline" data-testid="quick-client-btn" onClick={() => setAdding(true)}>+ {t("new_client")}</Button>
      </div>
      <div className="max-h-36 overflow-y-auto rounded-md border divide-y">
        {list.map((c) => (
          <button type="button" key={c.id} data-testid={`client-option-${c.id}`} onClick={() => onChange(c)}
            className="w-full text-left px-3 py-2 text-sm hover:bg-muted">
            {c.first_name} {c.last_name} <span className="text-muted-foreground">· {c.phone || c.email}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AppointmentForm({ open, onOpenChange, initial = {}, appointment, onSaved }) {
  const { locations, services, staff, locationId, user } = useApp();
  const { t, lang } = useT();
  const [f, setF] = useState({});
  const [client, setClient] = useState(null);
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const a = appointment || {};
    setF({
      location_id: a.location_id || initial.location_id || locationId || locations[0]?.id || "",
      service_id: a.service_id || "", professional_id: a.professional_id || initial.professional_id || (user.role === "professional" ? user.id : ""),
      date: a.date || initial.date, start_time: a.start_time || initial.start_time || "", notes: a.notes || "", send_email: !a.id,
    });
    setClient(a.client || (a.client_id ? { id: a.client_id, first_name: a.client_name, last_name: "" } : null));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const location = locations.find((l) => l.id === f.location_id);
  const svcList = services.filter((s) => s.active && s.prices?.[f.location_id]);
  const pros = staff.filter((s) => s.active && !s.home_only && s.work_mode !== "home" && ["professional", "owner"].includes(s.role) && s.location_ids?.includes(f.location_id));
  const svc = services.find((s) => s.id === f.service_id);

  useEffect(() => {
    if (!open || !f.location_id || !f.service_id || !f.professional_id || !f.date) { setSlots([]); return; }
    api.get("/slots", { params: { location_id: f.location_id, service_id: f.service_id, professional_id: f.professional_id, date: f.date } })
      .then((r) => setSlots(r.data.map((s) => s.time))).catch(() => setSlots([]));
  }, [open, f.location_id, f.service_id, f.professional_id, f.date]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, client_id: client.id };
      const { data } = appointment ? await api.put(`/appointments/${appointment.id}`, body) : await api.post("/appointments", body);
      toast.success(appointment ? "Appointment updated" : "Appointment created");
      if (f.send_email && data.emailed === false) toast.warning("Confirmation email could not be sent");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const timeOptions = Array.from(new Set([...(f.start_time ? [f.start_time] : []), ...slots])).sort();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="appointment-form">
        <DialogHeader><DialogTitle>{appointment ? t("edit") : t("new_appointment")}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <Field label={t("client")}><ClientPicker value={client} onChange={setClient} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("location")}>
              <NativeSelect testId="appt-location-select" value={f.location_id} onChange={(v) => setF({ ...f, location_id: v, service_id: "", start_time: "" })}
                options={locations.map((l) => ({ value: l.id, label: `${l.name} (${l.currency})` }))} />
            </Field>
            <Field label={t("date")}><Input data-testid="appt-date-input" type="date" value={f.date || ""} onChange={(e) => set("date")(e.target.value)} /></Field>
          </div>
          <Field label={t("service")}>
            <NativeSelect testId="appt-service-select" value={f.service_id} onChange={set("service_id")}
              options={[{ value: "", label: "—" }, ...svcList.map((s) => ({ value: s.id, label: `${s.name} · ${s.duration}${t("min")} · ${money(s.prices[f.location_id].price, location?.currency, lang)}` }))]} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("professional")}>
              <NativeSelect testId="appt-professional-select" value={f.professional_id} onChange={set("professional_id")}
                options={[{ value: "", label: "—" }, ...pros.map((p) => ({ value: p.id, label: p.name }))]} />
            </Field>
            <Field label={t("time")}>
              <NativeSelect testId="appt-time-select" value={f.start_time} onChange={set("start_time")}
                options={[{ value: "", label: timeOptions.length ? "—" : t("no_slots") }, ...timeOptions.map((s) => ({ value: s, label: s }))]} />
            </Field>
          </div>
          {svc && <p className="text-xs text-muted-foreground">{t("prep")}: {svc.prep} · {t("cleanup")}: {svc.cleanup}</p>}
          <Field label={t("notes")}><Textarea data-testid="appt-notes-input" value={f.notes || ""} onChange={(e) => set("notes")(e.target.value)} /></Field>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox data-testid="appt-send-email-checkbox" checked={!!f.send_email} onCheckedChange={(v) => set("send_email")(!!v)} />
            {t("send_email_conf")}
          </label>
          {client && "email" in client && !client.email && f.send_email && <p className="text-xs text-amber-700 -mt-2" data-testid="appt-no-email-hint">No email on file for this client</p>}
          <Button data-testid="appt-save-button" className="rounded-full" disabled={busy || !client || !f.service_id || !f.professional_id || !f.start_time} onClick={save}>
            {t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
